import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useDebounce from '../../hooks/useDebounce';

export default function SearchableSelect({ options, value, onChange, placeholder = 'Buscar...', required, disabled }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [posicao, setPosicao] = useState(null);
  const buscaDebounced = useDebounce(busca, 200);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selecionado = options.find((o) => o.value === value);
  const filtradas = options.filter((o) => o.label.toLowerCase().includes(buscaDebounced.toLowerCase()));

  useLayoutEffect(() => {
    if (!aberto || !triggerRef.current) return;
    const atualizar = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosicao({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    };
    atualizar();
    window.addEventListener('resize', atualizar);
    return () => window.removeEventListener('resize', atualizar);
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const fechar = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setAberto(false);
    };
    const fecharAoRolar = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    document.addEventListener('scroll', fecharAoRolar, true);
    return () => {
      document.removeEventListener('mousedown', fechar);
      document.removeEventListener('scroll', fecharAoRolar, true);
    };
  }, [aberto]);

  return (
    <div className="am-select">
      <button
        ref={triggerRef}
        type="button"
        className="am-select__trigger"
        disabled={disabled}
        onClick={() => setAberto((a) => !a)}
      >
        <span className="am-select__value">
          {selecionado ? (
            <>
              {selecionado.icon && <i className={`bi ${selecionado.icon} ${selecionado.iconColor || ''} me-1`} />}
              {selecionado.label}
            </>
          ) : (
            <span style={{ color: 'var(--am-placeholder)' }}>{placeholder}</span>
          )}
        </span>
        <span className="ms am-select__chevron">{aberto ? 'expand_less' : 'expand_more'}</span>
      </button>

      {required && (
        // opacity:0 (não display:none) de propósito — um input com display:none fica de fora da
        // validação nativa do form em navegadores Chromium e trava o submit sem nenhum aviso
        <input
          tabIndex={-1}
          required
          value={value || ''}
          onChange={() => {}}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
      )}

      {aberto &&
        posicao &&
        createPortal(
          <div
            ref={menuRef}
            className="am-select__menu"
            style={{ top: posicao.top, left: posicao.left, width: posicao.width }}
          >
            <input
              autoFocus
              type="text"
              className="am-select__search"
              placeholder={placeholder}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            <div className="am-select__opcoes">
              {filtradas.length === 0 && <div className="am-select__empty">Nenhum resultado</div>}
              {filtradas.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`am-select__opcao${o.value === value ? ' is-selected' : ''}`}
                  onClick={() => {
                    onChange(o.value, o);
                    setBusca('');
                    setAberto(false);
                  }}
                >
                  <span>
                    {o.icon && <i className={`bi ${o.icon} ${o.iconColor || ''} me-1`} />}
                    {o.label}
                  </span>
                  {o.value === value && <span className="ms">check</span>}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
